import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CDatePicker,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CLoadingButton,
  CMultiSelect,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import AdsManagerService from 'src/service/AdsManagerService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'
import { PLACEMENT_OPTIONS, formatDisplayDate, toApiDate } from './promoFormConfig'

const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif'
const MAX_IMAGE_SIZE = 5 * 1024 * 1024

const EditForm = ({ promoId, visible, onClose, fetchUsers }) => {
  const API_URL = process.env.REACT_APP_API_URL
  const [promoDetail, setPromoDetail] = useState({})
  const [errorMessages, setErrorMessages] = useState([])
  const [loader, setLoader] = useState(false)
  const [placementKey, setPlacementKey] = useState(0)

  const imageSchema = Yup.mixed()
    .nullable()
    .test('size', 'File size too large, max file size is 5 Mb', (value) =>
      value ? value.size <= MAX_IMAGE_SIZE : true,
    )
    .test(
      'type',
      'Invalid file format selection',
      (value) => !value || SUPPORTED_FORMATS.includes(value?.type),
    )

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required('Promo Title is required')
      .max(50, '50 Character Limit is allowed.'),
    validity_date: Yup.string()
      .required('Promo Validity date is required')
      .max(50, '50 Character Limit is allowed.'),
    placement: Yup.array()
      .min(1, 'Please select at least one promo placement')
      .required('Placement is required'),
    design_type: Yup.string()
      .required('Promo Label is required')
      .max(50, '50 Character Limit is allowed.'),
    description: Yup.string()
      .required('Promo Description is required')
      .test('word-limit', 'Maximum 50 words are allowed.', (value) => {
        if (!value) return true
        const wordCount = value
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length
        return wordCount <= 50
      }),
    image: imageSchema,
    logo: imageSchema,
  })

  const formik = useFormik({
    initialValues: {
      title: '',
      design_type: '',
      cta_text: '',
      cta_url: '',
      primary_color: '#000000',
      secondary_color: '#000000',
      validity_date: '',
      description: '',
      schedule_start_date: '',
      schedule_end_date: '',
      image: null,
      logo: null,
      placement: [],
    },
    validationSchema,
    onSubmit: (data) => {
      const formData = new FormData()
      formData.append('sponsorId', promoId)
      formData.append('title', data.title)
      formData.append('promo_label', data.design_type)
      formData.append('cta_text', data.cta_text)
      formData.append('primary_color', data.primary_color)
      formData.append('secondary_color', data.secondary_color)
      formData.append('validity_date', data.validity_date)
      formData.append('cta_url', data.cta_url)
      formData.append('schedule_start_date', data.schedule_start_date)
      formData.append('schedule_end_date', data.schedule_end_date)
      formData.append('description', data.description)
      // edit-ads stores the array as-is, so send the plain placement values.
      formData.append('placement', JSON.stringify(data.placement.map((item) => item.value)))

      if (data.image) {
        formData.append('image', data.image)
      }
      if (data.logo) {
        formData.append('logo', data.logo)
      }

      setLoader(true)
      AdsManagerService.editAds(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const { setValues } = formik

  useEffect(() => {
    if (!visible || !promoId) return

    AdsManagerService.getAdsDetail(promoId)
      .then((res) => {
        if (res.status === 200) {
          setPromoDetail(res.data)
          setValues({
            title: res.data?.title || '',
            design_type: res.data?.promo_label || '',
            cta_text: res.data?.cta_text || '',
            cta_url: res.data?.cta_url || '',
            primary_color: res.data?.primary_color || '#000000',
            secondary_color: res.data?.secondary_color || '#000000',
            validity_date: res.data?.validity_date || '',
            description: res.data?.description || '',
            schedule_start_date: res.data?.schedule_start_date || '',
            schedule_end_date: res.data?.schedule_end_date || '',
            image: null,
            logo: null,
            placement: PLACEMENT_OPTIONS.filter((option) =>
              (res.data?.placement || []).includes(option.value),
            ),
          })
          setPlacementKey((key) => key + 1)
        }
      })
      .catch(() => {
        ToastComponent('Something went wrong. Please try again.', 'error')
      })
  }, [visible, promoId, setValues])

  const handleClose = () => {
    setErrorMessages([])
    setPromoDetail({})
    formik.resetForm()
    onClose()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 900 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Promo Card</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update {promoDetail?.title || 'this promo card'} and where it appears.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
          <CCol xs={6}>
            <CFormLabel htmlFor="title">Promo Title *</CFormLabel>
            <CFormInput
              placeholder="Promo Title"
              className={
                'form-control' + (formik.errors.title && formik.touched.title ? ' is-invalid' : '')
              }
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              id="title"
              name="title"
            />
            {formik.errors.title && formik.touched.title && (
              <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="design_type">Promo Label *</CFormLabel>
            <CFormInput
              placeholder="Promo Label"
              className={
                'form-control' +
                (formik.errors.design_type && formik.touched.design_type ? ' is-invalid' : '')
              }
              value={formik.values.design_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              id="design_type"
              name="design_type"
            />
            {formik.errors.design_type && formik.touched.design_type && (
              <CFormFeedback invalid>{formik.errors.design_type}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="description">Promo Details *</CFormLabel>
            <CFormTextarea
              placeholder="Promo Details"
              rows={4}
              className={
                'form-control' +
                (formik.errors.description && formik.touched.description ? ' is-invalid' : '')
              }
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              id="description"
              name="description"
            />
            {formik.errors.description && formik.touched.description && (
              <CFormFeedback invalid>{formik.errors.description}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="image">Promo Image</CFormLabel>
            <ImageDropzone
              accept={ACCEPTED_IMAGE_TYPES}
              hint="330 x 120 px. JPG, PNG or GIF up to 5MB."
              minHeight={120}
              file={formik.values.image}
              existingUrl={promoDetail?.image ? `${API_URL}uploads/ads/${promoDetail.image}` : null}
              error={formik.touched.image ? formik.errors.image : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, image: true })
                formik.setFieldValue('image', selected)
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="logo">Promo Logo</CFormLabel>
            <ImageDropzone
              accept={ACCEPTED_IMAGE_TYPES}
              hint="500 x 500 px. JPG, PNG or GIF up to 5MB."
              minHeight={120}
              file={formik.values.logo}
              existingUrl={promoDetail?.logo ? `${API_URL}uploads/ads/${promoDetail.logo}` : null}
              error={formik.touched.logo ? formik.errors.logo : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, logo: true })
                formik.setFieldValue('logo', selected)
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="validity_date">Promo Validity *</CFormLabel>
            <CDatePicker
              locale="en-US"
              inputDateFormat={formatDisplayDate}
              placeholder="DD/MM/YYYY"
              date={formik.values.validity_date || null}
              onDateChange={(date) => formik.setFieldValue('validity_date', toApiDate(date))}
              onBlur={() => formik.setFieldTouched('validity_date', true)}
              className={
                formik.errors.validity_date && formik.touched.validity_date ? 'is-invalid' : ''
              }
            />
            {formik.errors.validity_date && formik.touched.validity_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.validity_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="placement">Promo Placement *</CFormLabel>
            <CMultiSelect
              key={placementKey}
              id="placement"
              options={PLACEMENT_OPTIONS}
              selectionType="tags"
              name="placement"
              value={formik.values.placement || []}
              onChange={(value) => formik.setFieldValue('placement', value)}
              onBlur={() => formik.setFieldTouched('placement', true)}
              className={formik.errors.placement && formik.touched.placement ? 'is-invalid' : ''}
            />
            {formik.errors.placement && formik.touched.placement && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.placement}
              </CFormFeedback>
            )}
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
            <h6 className="fw-bold mb-0 mt-3">Optional Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="cta_text">Promo CTA Text</CFormLabel>
            <CFormInput
              placeholder="Promo CTA Text"
              value={formik.values.cta_text}
              onChange={formik.handleChange}
              id="cta_text"
              name="cta_text"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="cta_url">Promo CTA Link</CFormLabel>
            <CFormInput
              placeholder="Promo CTA Link"
              value={formik.values.cta_url}
              onChange={formik.handleChange}
              id="cta_url"
              name="cta_url"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="primary_color">Primary Color</CFormLabel>
            <CFormInput
              type="color"
              value={formik.values.primary_color || '#000000'}
              onChange={formik.handleChange}
              name="primary_color"
              id="primary_color"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="primary_hex">Primary Hex Code</CFormLabel>
            <CFormInput
              type="text"
              name="primary_color"
              id="primary_hex"
              value={formik.values.primary_color || '#000000'}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="#000000"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="secondary_color">Secondary Color</CFormLabel>
            <CFormInput
              type="color"
              value={formik.values.secondary_color || '#000000'}
              onChange={formik.handleChange}
              name="secondary_color"
              id="secondary_color"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="secondary_hex">Secondary Hex Code</CFormLabel>
            <CFormInput
              type="text"
              name="secondary_color"
              id="secondary_hex"
              value={formik.values.secondary_color || '#000000'}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="#000000"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="schedule_start_date">Promo Start Date</CFormLabel>
            <CDatePicker
              locale="en-GB"
              inputDateFormat={formatDisplayDate}
              placeholder="DD/MM/YYYY"
              date={formik.values.schedule_start_date || null}
              onDateChange={(date) => formik.setFieldValue('schedule_start_date', toApiDate(date))}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="schedule_end_date">Promo End Date</CFormLabel>
            <CDatePicker
              locale="en-GB"
              inputDateFormat={formatDisplayDate}
              placeholder="DD/MM/YYYY"
              minDate={formik.values.schedule_start_date || undefined}
              date={formik.values.schedule_end_date || null}
              onDateChange={(date) => formik.setFieldValue('schedule_end_date', toApiDate(date))}
            />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
